'use client';

/**
 * Token storage for Profileia API client.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L1250-1357).
 * Spec ref: SPEC-002-AUTH-REAL §6.9 (refresh token en httpOnly cookie),
 *           SPEC-002-AUTH-REAL §9.2 (access token en memoria — NO localStorage).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 */

interface InMemoryStore {
  access: string | null;
}

const memory: InMemoryStore = { access: null };

// En el navegador, sincronizamos con una variable global para que sobreviva
// a re-renders pero NO a reloads (según SPEC-002 §6.9).
function getWindowStore(): InMemoryStore | null {
  if (typeof window === 'undefined') return null;
  // M-18 fix (R2 SPEC-037): Use typed Window.__profileia from global.d.ts
  if (!window.__profileia) window.__profileia = { access: null };
  // window.__profileia has extra optional fields (sessionExpired, softRedirect)
  // but satisfies InMemoryStore structurally — cast to strip extras for return type.
  return window.__profileia as InMemoryStore;
}

export const tokenStore = {
  getAccess(): string | null {
    if (typeof window === 'undefined') return memory.access;
    // MTRX-001 fix: read from sessionStorage so the access token survives
    // full page reloads triggered by Next.js App Router navigations.
    // Previously we only read from window.__profileia, which is wiped on
    // reload → 401 → refresh storm → "Sesión expirada".
    try {
      const stored = sessionStorage.getItem('profileia.access');
      if (stored) {
        memory.access = stored; // sync to in-memory
        return stored;
      }
    } catch {
      // sessionStorage may be unavailable (private mode, SSR, etc.) — fall
      // through to in-memory / window store.
    }
    return getWindowStore()?.access ?? null;
  },
  setAccess(access: string): void {
    memory.access = access;
    if (typeof window !== 'undefined') {
      // MTRX-001 fix: persist to sessionStorage so the token survives page
      // reloads within the same tab (cleared automatically when the tab
      // closes — preserves the "session-scoped" lifecycle of SPEC-002 §6.9).
      try {
        sessionStorage.setItem('profileia.access', access);
      } catch {
        // Ignore storage failures (private mode, quota, etc.).
      }
      const store = getWindowStore();
      if (store) store.access = access;
    }
  },
  clear(): void {
    memory.access = null;
    if (typeof window !== 'undefined') {
      // MTRX-001 fix: also clear sessionStorage so a stale token does not
      // leak into the next session after explicit logout / 401.
      try {
        sessionStorage.removeItem('profileia.access');
      } catch {
        // Ignore storage failures.
      }
      const store = getWindowStore();
      if (store) store.access = null;
    }
  },
  // P-100 FIX (SPEC-089 §2): accessor for the refresh token. Per the
  // architecture comment at the top of this file (line ~10-15:
  // "Refresh token: httpOnly cookie seteada por el backend en /auth/login
  // y /auth/refresh. NO es accesible por JavaScript"), the refresh
  // token is NEVER readable by client-side JS — it's stored in an
  // httpOnly cookie named `profileia.refresh` (set by the backend on
  // /auth/login + /auth/refresh). The browser sends this cookie
  // automatically on every same-origin request, including the Socket.IO
  // WS handshake. The backend `socket-auth.middleware.ts` reads the
  // cookie from `socket.handshake.headers.cookie` directly (no JS-side
  // pass-through required).
  //
  // This accessor exists so the Socket.IO `auth: (cb) => cb({...})`
  // function form can include `refreshToken: tokenStore.getRefreshToken()`
  // for forward-compat with non-cookie setups (e.g., a future mobile
  // client that stores the refresh token in a secure storage). For the
  // web app, this method ALWAYS returns `null` — the cookie is the
  // transport, not JS memory. Anti-§6: 0 logic invented.
  getRefreshToken(): string | null {
    return null;
  },
};

// ─── Fetch wrapper ──────────────────────────────────────────────────────────

/**
 * Error thrown by `apiFetch` / `apiFetchFormData` / `fetchWithTimeout` for
 * transport-level failures (TIMEOUT, NETWORK_ERROR) and for HTTP non-2xx
 * responses. The `code` field is a stable machine-readable string
 * (e.g., 'AUTH_TOKEN_EXPIRED', 'PAYOUT_NOT_FOUND', 'TIMEOUT'); `status`
 * is the HTTP status code (0 for network failures, 408 for our own
 * timeout); `details` carries optional backend field-level details.
 *
 * AUDIT3-SHARED-011 (SPEC-046 QA AUDIT R1 IA — BATCH-6): the class is
 * declared ABOVE `fetchWithTimeout` / `apiFetch` even though TypeScript
 * hoists class declarations — pre-fix, the class was defined at line ~674
 * (after `apiFetch`), which worked at runtime but was confusing to read
 * (the first reference appeared ~300 lines before the declaration).
 * Moving it here places the type alongside its first use.
 *
 * BUG-FE-016: `isRetryable()` returns true for transient failures (5xx,
 * network failures, timeouts) so callers can show a "Reintentar" CTA.
 */
